import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatDetail } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, StatusBadge, KpiCard, EmptyState } from "@/components/ui-parts";
import { ArrowLeft, Bot, Brain, Car, FileText, MessageSquare, Sparkles, Save, X, Unlink } from "lucide-react";
import { toast } from "sonner";

const fmtEUR = (v) =>
  v == null ? "—" : new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v));
const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(String(v)).toLocaleDateString("de-DE");
  } catch {
    return String(v);
  }
};
const fmtDateTime = (v) => {
  if (!v) return "—";
  try {
    return new Date(String(v)).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(v);
  }
};
const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)} %`);

export default function MemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [schema, setSchema] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editable fields (mirroring backend PATCH)
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("entry");
  const [allowedCompanies, setAllowedCompanies] = useState([]);
  const [scope, setScope] = useState("all");
  const [perms, setPerms] = useState({});
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [p, s, c] = await Promise.all([
        api.get(`/team/${id}/profile`),
        api.get("/team/bot-permissions/schema").catch(() => ({ data: [] })),
        api.get("/companies"),
      ]);
      setData(p.data);
      setSchema(s.data || []);
      setCompanies(c.data);
      const m = p.data.member || {};
      setName(m.name || "");
      setPhone(m.phone || "");
      setRole(m.role || "entry");
      setAllowedCompanies(m.allowed_company_ids || []);
      setScope((m.allowed_company_ids || []).length === 0 ? "all" : "custom");
      setPerms(m.bot_permissions || {});
      setNotes(m.admin_notes || "");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Konnte Profil nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/team/${id}`, {
        name,
        phone: phone.trim(),
        role,
        allowed_company_ids: scope === "all" ? [] : allowedCompanies,
        bot_permissions: perms,
        admin_notes: notes,
      });
      toast.success("Änderungen gespeichert");
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const unlinkTelegram = async () => {
    if (!window.confirm("Telegram-Verknüpfung aufheben? Der Mitarbeiter muss sich neu registrieren.")) return;
    try {
      await api.patch(`/team/${id}`, { unlink_telegram: true });
      toast.success("Telegram-Verknüpfung entfernt");
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  if (loading) {
    return <div className="px-8 py-8 text-sm text-[#5C5E60]">Lade Profil…</div>;
  }
  if (!data) {
    return <div className="px-8 py-8 text-sm text-[#B31D1D]">Mitglied nicht gefunden.</div>;
  }

  const { member, stats, recent_receipts, recent_dialogs, vehicles, ai_insights } = data;
  const toggleCompany = (cid) =>
    setAllowedCompanies((cur) => (cur.includes(cid) ? cur.filter((x) => x !== cid) : [...cur, cid]));

  return (
    <div>
      <PageHeader
        title={member.name || member.email}
        subtitle={`${member.email} · ${member.role === "owner" ? "Inhaber" : member.role === "accountant" ? "Buchhalter" : "Erfasser"}`}
        actions={
          <div className="flex items-center gap-2">
            <SecondaryButton onClick={() => navigate("/app/team")} data-testid="back-to-team">
              <ArrowLeft className="h-4 w-4" /> Zurück
            </SecondaryButton>
            <PrimaryButton onClick={save} disabled={saving} data-testid="save-profile">
              <Save className="h-4 w-4" /> {saving ? "Speichere…" : "Speichern"}
            </PrimaryButton>
          </div>
        }
      />

      <div className="px-8 py-8 space-y-8">
        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-row">
          <KpiCard label="Belege gesamt" value={stats.receipts_total} sublabel={`${stats.receipts_matched} zugeordnet`} />
          <KpiCard label="Offen" value={stats.receipts_unmatched} tone={stats.receipts_unmatched > 0 ? "warning" : "default"} />
          <KpiCard label="Anfragen" value={stats.requests_total} sublabel={`${stats.requests_fulfilled} erledigt`} />
          <KpiCard label="KI-Rückfragen" value={stats.ai_dialogs_total} sublabel={`${stats.ai_dialogs_pending} offen`} />
        </div>

        {/* Stammdaten + Bot-Berechtigungen */}
        <section className="bg-white border border-[#E6E6E4] rounded-md p-6 space-y-6" data-testid="core-fields">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-bold text-lg text-[#1A1A1A]">Stammdaten & Berechtigungen</div>
              <div className="text-xs text-[#5C5E60]">
                Alle Angaben aus der Einladung + Bot-Rechte. Änderungen werden mit &bdquo;Speichern&ldquo; oben übernommen.
              </div>
            </div>
            <StatusBadge variant={member.status === "active" ? "success" : "warning"}>
              {member.status === "active" ? "Aktiv" : member.status === "pending" ? "Ausstehend" : "Widerrufen"}
            </StatusBadge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-name"
                className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Telefon (Telegram-Whitelist)</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+49 170 …" data-testid="edit-phone"
                className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
              {member.telegram_user_id && (
                <div className="mt-1 flex items-center gap-2 text-[11px] text-[#0F5132]">
                  <MessageSquare className="h-3 w-3" />
                  Telegram verknüpft{member.telegram_username ? ` · @${member.telegram_username}` : ""}
                  <button onClick={unlinkTelegram} data-testid="unlink-tg"
                    className="ml-2 inline-flex items-center gap-1 text-[#B31D1D] hover:underline">
                    <Unlink className="h-3 w-3" /> aufheben
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Rolle</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="edit-role"
                className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm bg-white hover:border-[#18322B]">
                <option value="accountant">Buchhalter</option>
                <option value="entry">Erfasser</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Interne Notiz (nur Admin sichtbar)</label>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="z.B. „nur Barzahler, montags nicht erreichbar"
                data-testid="edit-notes"
                className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Firmen-Zugriff</label>
            <div className="flex items-center gap-4 text-sm">
              <label className="inline-flex items-center gap-2">
                <input type="radio" checked={scope === "all"} onChange={() => setScope("all")} />
                Alle Firmen
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="radio" checked={scope === "custom"} onChange={() => setScope("custom")} />
                Nur ausgewählte
              </label>
            </div>
            {scope === "custom" && (
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {companies.map((c) => (
                  <label key={c.id}
                    className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer text-sm transition-colors ${
                      allowedCompanies.includes(c.id) ? "border-[#18322B] bg-[#E4F0E8]" : "border-[#E6E6E4]"
                    }`}>
                    <input type="checkbox" checked={allowedCompanies.includes(c.id)} onChange={() => toggleCompany(c.id)} />
                    <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
                    <span className="text-[#1A1A1A] truncate">{c.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {schema.length > 0 && (
            <div className="pt-4 border-t border-[#E6E6E4]">
              <div className="flex items-center gap-2 mb-3">
                <Bot className="h-4 w-4 text-[#18322B]" strokeWidth={2} />
                <div className="font-display font-bold text-sm text-[#1A1A1A]">Bot-Berechtigungen</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="bot-perms-grid">
                {schema.map((p) => {
                  const on = !!perms[p.key];
                  return (
                    <label key={p.key}
                      className={`flex items-start gap-2 px-3 py-2 border rounded-md cursor-pointer text-xs transition-colors ${
                        on ? "border-[#18322B] bg-[#E4F0E8]" : "border-[#E6E6E4] bg-white"
                      } ${p.sensitive ? "border-l-4 border-l-[#B31D1D]" : ""}`}>
                      <input type="checkbox" checked={on}
                        onChange={(ev) => setPerms((prev) => ({ ...prev, [p.key]: ev.target.checked }))}
                        data-testid={`perm-${p.key}`} className="mt-0.5" />
                      <div className="flex-1">
                        <div className="text-[#1A1A1A] font-semibold">
                          {p.label}
                          {p.sensitive && (
                            <span className="ml-1 text-[10px] uppercase tracking-wider text-[#B31D1D]">sensibel</span>
                          )}
                        </div>
                        <div className="text-[10px] text-[#5C5E60] mt-0.5">{p.description}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* KI-Schlussfolgerungen */}
        <section className="bg-[#F9F9F8] border border-[#E6E6E4] rounded-md p-6" data-testid="ai-insights">
          <div className="flex items-center gap-2 mb-1">
            <Brain className="h-4 w-4 text-[#8B4A0E]" strokeWidth={2} />
            <div className="font-display font-bold text-base text-[#1A1A1A]">KI-Schlussfolgerungen</div>
            <span className="text-[10px] uppercase tracking-wider text-[#5C5E60] ml-2">automatisch abgeleitet</span>
          </div>
          <p className="text-xs text-[#5C5E60] mb-4">
            Diese Werte werden aus den Belegen, Anfragen und Rückfragen dieses Mitarbeiters berechnet – sie sind keine
            Eingaben.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InsightBlock title="Häufigste Lieferanten" items={ai_insights.top_vendors} emptyLabel="Noch keine Belege." />
            <InsightBlock title="Häufigste Kategorien" items={ai_insights.top_categories} emptyLabel="Noch keine Belege." />
            <InsightBlock title="Häufigste Kennzeichen" items={ai_insights.top_plates} emptyLabel="Keine erkannt." />
            <MetricBlock label="Ø Verzögerung Beleg → Upload"
              value={ai_insights.avg_submit_delay_days == null ? "—" : `${ai_insights.avg_submit_delay_days} Tage`}
              hint="Zeit zwischen Rechnungsdatum und Einreichung." />
            <MetricBlock label="Auffälligkeitsquote"
              value={pct(ai_insights.concern_rate)}
              hint="Anteil Belege mit KI-Rückfrage. Niedrig = zuverlässige Belege." />
            <MetricBlock label="Anfrage-Erfüllung"
              value={pct(ai_insights.fulfillment_rate)}
              hint="Wie zuverlässig fehlende Belege nachgereicht werden." />
          </div>
          <div className="mt-4 text-[11px] text-[#5C5E60]">
            Letzte Aktivität: <span className="font-mono-tab text-[#1A1A1A]">{fmtDateTime(ai_insights.last_upload_at)}</span>
          </div>
        </section>

        {/* Recent receipts */}
        <section className="bg-white border border-[#E6E6E4] rounded-md p-6" data-testid="recent-receipts">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-[#18322B]" strokeWidth={2} />
            <div className="font-display font-bold text-base text-[#1A1A1A]">Letzte Belege</div>
          </div>
          {recent_receipts.length === 0 ? (
            <EmptyState title="Noch keine Belege" hint="Sobald der Mitarbeiter etwas einreicht, erscheint es hier." />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] border-b border-[#E6E6E4]">
                  <th className="text-left py-2 font-semibold">Datum</th>
                  <th className="text-left py-2 font-semibold">Lieferant</th>
                  <th className="text-left py-2 font-semibold">Kategorie</th>
                  <th className="text-right py-2 font-semibold">Betrag</th>
                  <th className="text-right py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {recent_receipts.map((r) => (
                  <tr key={r.id} className="border-b border-[#E6E6E4] last:border-0">
                    <td className="py-2 text-[#1A1A1A]">{fmtDate(r.date)}</td>
                    <td className="py-2 text-[#1A1A1A]">{r.vendor || r.filename || "—"}</td>
                    <td className="py-2 text-[#5C5E60]">{r.category || "—"}</td>
                    <td className="py-2 text-right font-mono-tab text-[#1A1A1A]">{fmtEUR(r.total_amount)}</td>
                    <td className="py-2 text-right">
                      {r.matched ? (
                        <StatusBadge variant="success">Zugeordnet</StatusBadge>
                      ) : (
                        <StatusBadge variant="warning">Offen</StatusBadge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* KI-Rückfragen */}
        <section className="bg-white border border-[#E6E6E4] rounded-md p-6" data-testid="ai-dialogs">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#8B4A0E]" strokeWidth={2} />
              <div className="font-display font-bold text-base text-[#1A1A1A]">KI-Rückfragen an diesen Mitarbeiter</div>
            </div>
            <Link to="/app/ki-pruefung" className="text-xs text-[#18322B] hover:underline">
              Alle Dialoge verwalten →
            </Link>
          </div>
          {recent_dialogs.length === 0 ? (
            <EmptyState title="Noch keine Rückfragen" hint="Sobald der Bot Zweifel hat, erscheinen die Fragen hier." />
          ) : (
            <div className="space-y-3">
              {recent_dialogs.map((d) => (
                <div key={d.id} className="border border-[#E6E6E4] rounded-md p-3 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-[#5C5E60]">{d.kind}</span>
                      {d.severity === "warn" && <StatusBadge variant="warning">Wichtig</StatusBadge>}
                      <StatusBadge variant={d.status === "approved" ? "success" : d.status === "rejected" ? "error" : "warning"}>
                        {d.status === "approved" ? "Bestätigt" : d.status === "rejected" ? "Verworfen" : "Offen"}
                      </StatusBadge>
                    </div>
                    <span className="text-[11px] text-[#5C5E60]">{fmtDateTime(d.created_at)}</span>
                  </div>
                  <div className="text-[#1A1A1A] whitespace-pre-wrap">{d.question}</div>
                  {d.answer && (
                    <div className="mt-2 text-[#0F5132] text-xs border-l-2 border-[#0F5132]/40 pl-2">
                      Antwort: {d.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Vehicles */}
        <section className="bg-white border border-[#E6E6E4] rounded-md p-6" data-testid="vehicles">
          <div className="flex items-center gap-2 mb-3">
            <Car className="h-4 w-4 text-[#18322B]" strokeWidth={2} />
            <div className="font-display font-bold text-base text-[#1A1A1A]">Zugewiesene Fahrzeuge</div>
          </div>
          {vehicles.length === 0 ? (
            <EmptyState title="Keine Fahrzeuge" hint="Sobald ein Tank- oder Werkstattbeleg ein Kennzeichen enthält, wird es hier verknüpft." />
          ) : (
            <div className="flex flex-wrap gap-2">
              {vehicles.map((v) => (
                <div key={v.id} className="inline-flex items-center gap-2 px-3 py-2 border border-[#E6E6E4] rounded-md text-sm">
                  <span className="font-mono-tab text-[#1A1A1A]">{v.license_plate}</span>
                  <span className="text-xs text-[#5C5E60]">
                    {v.current_km ? `${v.current_km} km` : "—"}
                    {v.auto_created && " · KI-erfasst"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function InsightBlock({ title, items, emptyLabel }) {
  return (
    <div className="bg-white border border-[#E6E6E4] rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#5C5E60] mb-2">{title}</div>
      {items?.length ? (
        <div className="space-y-1">
          {items.map((it) => (
            <div key={it.label} className="flex justify-between text-sm">
              <span className="text-[#1A1A1A] truncate mr-2">{it.label}</span>
              <span className="text-[#5C5E60] font-mono-tab">{it.count}×</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-[#5C5E60]">{emptyLabel}</div>
      )}
    </div>
  );
}

function MetricBlock({ label, value, hint }) {
  return (
    <div className="bg-white border border-[#E6E6E4] rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#5C5E60] mb-1">{label}</div>
      <div className="font-display font-bold text-2xl text-[#1A1A1A]">{value}</div>
      <div className="text-[11px] text-[#5C5E60] mt-1">{hint}</div>
    </div>
  );
}
